package org.teddapp.project.db.repo

import com.tedd.db.Account
import com.tedd.db.TeddDatabase
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.withContext
import org.teddapp.project.auth.PasswordHasher
import org.teddapp.project.db.newId
import org.teddapp.project.db.nowMillis

/**
 * Accès et opérations sur le compte local (au plus un compte par installation en V1).
 * À terme, mappera vers un utilisateur Supabase Auth — pour l'instant, hash local.
 */
class AccountRepository(private val db: TeddDatabase) {

    /** Le compte courant s'il existe (V1 : un seul compte local). */
    suspend fun current(): Account? = withContext(Dispatchers.Default) {
        db.accountQueries.getCurrent().executeAsOneOrNull()
    }

    /** Existe-t-il déjà un compte local ? */
    suspend fun hasAccount(): Boolean = withContext(Dispatchers.Default) {
        db.accountQueries.count().executeAsOne() > 0L
    }

    /**
     * Crée un compte. **Un seul compte par installation** — toute tentative
     * de signup alors qu'un compte existe déjà est refusée (peu importe l'email).
     * Sur succès, marque biometric_enrolled = 1 (l'utilisateur vient de définir
     * un mot de passe, l'empreinte devient un raccourci utilisable à la prochaine connexion).
     */
    suspend fun signup(email: String, password: String): SignupResult = withContext(Dispatchers.Default) {
        if (db.accountQueries.count().executeAsOne() > 0L) {
            return@withContext SignupResult.AlreadyHasAccount
        }

        val salt = PasswordHasher.randomSalt()
        val hash = PasswordHasher.hash(password, salt)
        val id = newId("acc")
        val now = nowMillis()
        db.accountQueries.insert(
            id = id,
            email = email,
            password_hash = hash,
            salt = salt,
            biometric_enrolled = 1L,   // enrolled à la création — utilisable dès le prochain démarrage
            created_at = now,
        )
        db.accountQueries.updateLastLogin(now, id)
        SignupResult.Success
    }

    /**
     * Le serveur a accepté ces identifiants : le compte de l'appareil les prend, qu'il
     * existe déjà sous un autre nom ou pas encore. Un seul mot de passe pour tout.
     */
    suspend fun adopt(email: String, password: String): Account? = withContext(Dispatchers.Default) {
        val salt = PasswordHasher.randomSalt()
        val hash = PasswordHasher.hash(password, salt)
        val now = nowMillis()
        val existing = db.accountQueries.getCurrent().executeAsOneOrNull()
        if (existing != null) {
            db.accountQueries.updateCredentials(email, hash, salt, existing.id)
            db.accountQueries.updateLastLogin(now, existing.id)
        } else {
            db.accountQueries.insert(id = newId("acc"), email = email, password_hash = hash, salt = salt, biometric_enrolled = 1L, created_at = now)
        }
        db.accountQueries.selectByEmail(email).executeAsOneOrNull()?.also { db.accountQueries.updateLastLogin(now, it.id) }
    }

    /** Vérifie email + mot de passe contre la DB locale. */
    suspend fun loginWithPassword(email: String, password: String): LoginResult = withContext(Dispatchers.Default) {
        val account = db.accountQueries.selectByEmail(email).executeAsOneOrNull()
            ?: return@withContext LoginResult.NoSuchAccount
        if (!PasswordHasher.verify(password, account.salt, account.password_hash)) {
            return@withContext LoginResult.WrongPassword
        }
        db.accountQueries.updateLastLogin(nowMillis(), account.id)
        // Premier login après signup → enrôle l'empreinte si pas déjà fait
        if (account.biometric_enrolled == 0L) {
            db.accountQueries.markBiometricEnrolled(account.id)
        }
        LoginResult.Success(account)
    }

    /**
     * Authentification biométrique. En V1 (pas d'API Touch ID branchée) :
     * accepte si un compte existe ET que biometric_enrolled = 1. La sécurité réelle
     * arrive quand on branchera Touch ID / Windows Hello / Android BiometricPrompt.
     */
    suspend fun loginWithBiometric(): LoginResult = withContext(Dispatchers.Default) {
        val account = db.accountQueries.getCurrent().executeAsOneOrNull()
            ?: return@withContext LoginResult.NoSuchAccount
        if (account.biometric_enrolled == 0L) return@withContext LoginResult.BiometricNotEnrolled
        db.accountQueries.updateLastLogin(nowMillis(), account.id)
        LoginResult.Success(account)
    }
}

sealed interface SignupResult {
    data object Success : SignupResult
    /** Un compte existe déjà sur cet appareil (V1 : un seul compte autorisé). */
    data object AlreadyHasAccount : SignupResult
}

sealed interface LoginResult {
    data class Success(val account: Account) : LoginResult
    data object NoSuchAccount : LoginResult
    data object WrongPassword : LoginResult
    data object BiometricNotEnrolled : LoginResult
}
